import { PIIRedactor } from 'services/observability/datadog';
import { TOAST_VARIANTS } from 'ui/toast/constants';
import { safelyStringifyObject } from 'utils/stringHelpers';
import { addToastRequest, removeAllToastsRequest, removeToastByKeyRequest } from './actions';
import { ErrorActionType, ErrorIdentifierType } from './types';

const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';

type ToastMessage = React.ReactNode;
/**
 * addToast(message, variant, otherProps)<br>
 * Adds a toast
 * @param { String } message: The message to show in the toast.
 * @param { String } variant: The toast variant.
 * @param { Object } otherProps: Additional props for the toast.
 */
const addToast = (
  message: ToastMessage,
  variant: TOAST_VARIANTS,
  otherProps: Record<string, any>,
) => addToastRequest({ children: message, variant, ...otherProps });

/**
 * addPositiveToast(message, otherProps)<br>
 * Adds a Positive toast.
 * @param { String } message: The message to show in the toast.
 * @param { Object } otherProps: Additional props for the toast. See `addToast` otherProps.
 * @return { void }
 */
export const addPositiveToast = (message: ToastMessage, otherProps: Record<string, any> = {}) =>
  addToast(message, TOAST_VARIANTS.POSITIVE, otherProps);

/**
 * addWarningToast(message, otherProps)<br>
 * Adds a warning toast.
 * @param { String } message: The message to show in the toast.
 * @param { Object } otherProps: Additional props for the toast. See `addToast` otherProps.
 * @return { void }
 */
export const addWarningToast = (message: ToastMessage, otherProps: Record<string, any> = {}) =>
  addToast(message, TOAST_VARIANTS.WARNING, otherProps);

export const addInfoToast = (message: ToastMessage, otherProps: Record<string, any> = {}) =>
  addToast(message, TOAST_VARIANTS.INFO, otherProps);
/**
 * addNegativeToast(message, otherProps)<br>
 * Adds an Negative toast.
 * @param { String } message: The message to show in the toast.
 * @param { Object } otherProps: Additional props for the toast. See `addToast` otherProps.
 * @return { void }
 */
export const addNegativeToast = (
  message: ToastMessage = GENERIC_ERROR_MESSAGE,
  otherProps: Record<string, any> = { noDelay: true, errorIdentifier: {} },
) => addToast(message, TOAST_VARIANTS.NEGATIVE, otherProps);

export const removeAllToasts = removeAllToastsRequest;
export const removeToastKey = (key: string) => removeToastByKeyRequest({ key });

export const errorIdentifier = (obj: ErrorIdentifierType, actions?: Array<ErrorActionType>) => {
  let errorObject;

  try {
    const errorStringStrippedOfPII = PIIRedactor.fromString(JSON.stringify(obj));

    errorObject = JSON.parse(errorStringStrippedOfPII);
  } catch (e) {
    errorObject = {};
  }

  return {
    errorIdentifier: errorObject,
    actions: actions || [],
  };
};

/**
 * Creates a toast payload as well as add errorIdentifier properties
 */
export const addNegativeToastWithErrorId = (
  errorMessage: ToastMessage,
  source: string,
  error?: unknown,
  additional: unknown = {},
  actions = [] as ErrorActionType[],
) => {
  const eId: ErrorIdentifierType = {
    errorMessage: safelyStringifyObject(errorMessage),
    source,
    error,
    additional,
  };

  return addNegativeToast(errorMessage, errorIdentifier(eId, actions));
};
